import { SystemOptionType } from 'data/types';

export type OperatingCostParts = {
  coolingCost: number | null;
  heatingCost: number | null;
  auxHeatingCost: number | null;
};

type CostedSystem = {
  coolingCost: number | null;
  heatingCost: number | null;
  auxHeatingCost: number | null;
};

export type AuxHeatCostLine = {
  label: string;
  sublabel: string;
  annualCost: number;
};

export type AuxHeatCostLineInput = {
  auxHeatingCost: number | null | undefined;
  heatingRequiredRatio: number | null | undefined;
};

function sumCost(
  systems: readonly CostedSystem[],
  key: keyof CostedSystem
): number | null {
  if (systems.some((system) => system[key] == null)) return null;
  return systems.reduce((total, system) => total + (system[key] ?? 0), 0);
}

export function summedOperatingCostParts(
  systems: readonly CostedSystem[]
): OperatingCostParts {
  return {
    coolingCost: sumCost(systems, 'coolingCost'),
    heatingCost: sumCost(systems, 'heatingCost'),
    auxHeatingCost: sumCost(systems, 'auxHeatingCost')
  };
}

type OptionWithAuxCost = SystemOptionType & {
  auxHeatingCost?: number | null;
  heatingRequiredRatio?: number;
};

export function auxHeatCostLine(
  input: AuxHeatCostLineInput
): AuxHeatCostLine | null {
  const annualCost = input.auxHeatingCost ?? null;
  if (annualCost === null || annualCost <= 0) return null;
  const ratio = input.heatingRequiredRatio;
  if (typeof ratio === 'number' && ratio < 1) {
    return {
      label: 'Heat from your existing system',
      sublabel:
        "(Estimated at electric rates for the hours this system doesn't cover; your actual fuel cost may differ)",
      annualCost
    };
  }
  return {
    label: 'Backup heat',
    sublabel:
      '(Estimated, electric resistance, for the heating hours this system cannot cover)',
    annualCost
  };
}

export function auxHeatCostLineForOption(
  system: SystemOptionType
): AuxHeatCostLine | null {
  const option = system as OptionWithAuxCost;
  return auxHeatCostLine({
    auxHeatingCost: option.auxHeatingCost,
    heatingRequiredRatio: option.heatingRequiredRatio
  });
}
